/** @jsxImportSource @emotion/react */
import { ReactElement, useCallback, useMemo } from 'react'
import { useField, useForm } from 'react-final-form'
import { FieldValidator } from 'final-form'
import Button from '@mui/material/Button'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import * as classes from './styles'

export type RenderAdvancedFieldProps<Item> = {
  name: string
  index: number
  value: Item | null
  onChange: (value: Item | null) => void
  editable: boolean
  error?: string | string[]
}

export type AdvancedFieldProps<Item> = {
  name: string
  index: number
  renderField: (props: RenderAdvancedFieldProps<Item>) => ReactElement | ReactElement[]
  removeDisabled?: boolean
  onRemove: (value: Item | null, index: number) => void
  empty?: boolean
  editable?: boolean
  onEditableChange: (value: boolean, index: number) => void
  tempValue: Item | null
  onTempValueChange: (tempValue: Item | null, index: number) => void
  validate?: FieldValidator<Item>
  alwaysEditable?: boolean
  className?: string
  isSortable?: boolean
}

export function AdvancedField<Item>({
  name,
  index,
  renderField,
  removeDisabled,
  onRemove,
  empty,
  editable,
  onEditableChange,
  tempValue,
  onTempValueChange,
  validate,
  alwaysEditable,
  className,
  isSortable,
  ...props
}: AdvancedFieldProps<Item>) {
  const form = useForm()
  const { input, meta } = useField<Item>(name, { validate })

  const handleChange = useCallback(
    (value: Item | null) => {
      if (alwaysEditable) {
        form.change(name, value ?? undefined)
        return
      }

      if (editable) {
        onTempValueChange(value, index)
        return
      }
    },
    [index, name, editable, alwaysEditable, form.change]
  )

  const handleCancel = useCallback(() => {
    onEditableChange(false, index)
    onTempValueChange(input.value ?? null, index)
  }, [index, input.value])

  const handleConfirm = useCallback(() => {
    const isError = validate?.(tempValue as Item, form.getState().values, undefined)

    if (isError) {
      input.onBlur()
      return
    }

    onEditableChange(false, index)
    form.change(name, tempValue ?? undefined)
  }, [index, name, tempValue])

  const tempValueErrorMessage = useMemo(
    () => validate?.(tempValue as Item, form.getState().values, undefined),
    [tempValue]
  )
  const tempValueError = meta.touched && tempValueErrorMessage
  const error = meta.touched && meta.error

  return (
    <div className={className} css={classes.item(editable && tempValueError ? false : error)} {...props}>
      <div css={classes.content}>
        {renderField({
          name,
          index,
          value: editable ? tempValue : input.value ?? null,
          onChange: handleChange,
          editable: Boolean(editable || alwaysEditable),
          error: editable ? tempValueError : error,
        })}
      </div>
      {(!editable || empty) && (
        <Button
          variant="outlined"
          color="red"
          size="small"
          onClick={() => onRemove(input.value ?? null, index)}
          disabled={removeDisabled}
          css={classes.iconButton}
          data-testid="remove-item-button"
        >
          <TrashIcon />
        </Button>
      )}
      {!editable && !alwaysEditable && (
        <Button
          variant="outlined"
          color="gray"
          size="small"
          onClick={() => onEditableChange(true, index)}
          css={classes.iconButton}
          data-testid="edit-item-button"
        >
          <EditIcon />
        </Button>
      )}
      {editable && !alwaysEditable && (
        <>
          {!empty && (
            <Button
              variant="outlined"
              color="red"
              size="small"
              onClick={handleCancel}
              css={classes.iconButton}
              data-testid="cross-item-button"
            >
              <CrossIcon />
            </Button>
          )}
          <Button
            variant="outlined"
            color="green"
            size="small"
            onClick={handleConfirm}
            css={classes.iconButton}
            data-testid="check-item-button"
          >
            <CheckIcon />
          </Button>
        </>
      )}
      {isSortable && <DnDIcon className="field-dnd-icon" data-testid="field-dnd-icon" />}
    </div>
  )
}
